import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { 
  LayoutDashboard, 
  Key, 
  Tag, 
  CreditCard, 
  Calendar, 
  History, 
  Lock, 
  Save, 
  CheckCircle, 
  AlertCircle,
  ShieldAlert,
  LogOut,
  Menu,
  X,
  RefreshCw
} from "lucide-react";
import { useAuthActions, useConvexAuth } from "@convex-dev/auth/react";
import { parseFriendlyError } from "@/lib/errorHandler";
import { getClientIp, getClientBrowserInfo } from "@/lib/auditHelper";
import { useNavigate, Navigate } from "react-router-dom";

export default function SuperadminDashboard() {
  const navigate = useNavigate();
  const { signOut } = useAuthActions();
  const { isAuthenticated, isLoading } = useConvexAuth();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "overview" | "apikeys" | "pricing" | "midtrans" | "events" | "logs" | "password"
  >("overview");

  // Guard: kalau belum auth, cek session lokal admin terlebih dahulu
  const hasLocalSession = !!localStorage.getItem("fotomomen_admin_email");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  // Belum ada sesi sama sekali (Convex maupun lokal), redirect ke halaman login
  if (!isAuthenticated && !hasLocalSession) {
    return <Navigate to="/admin/login" replace />;
  }
  // Kalau sudah auth (salah satu lolos), lanjut render dashboard di bawah

  // Local state for forms
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Queries - kirim adminEmail jika dari fallback session
  const adminEmail = localStorage.getItem("fotomomen_admin_email");
  // M-1: skip semua query admin saat logout in-flight (signingOut) atau sudah sign out
  const adminAuthed = (isAuthenticated || !!adminEmail) && !signingOut;
  const authArgs: any = adminAuthed
    ? (isAuthenticated ? {} : { adminEmail: adminEmail ?? undefined })
    : "skip";
  const stats = useQuery(api.admin.getAdminStats, authArgs);
  const settings = useQuery(api.admin.getPlatformSettings);
  const allEvents = useQuery(api.admin.listAllEvents, authArgs);
  const auditLogsArgs: any = adminAuthed ? { ...authArgs, limit: 50 } : "skip";
  const auditLogs = useQuery(api.admin.listAuditLogs, auditLogsArgs);

  // Mutations
  const updateSettings = useMutation(api.admin.updatePlatformSettings);

  // State forms
  const [pricingForm, setPricingForm] = useState({
    pro: 99000,
    enterprise: 499000,
  });

  const [apiKeysForm, setApiKeysForm] = useState({
    midtransServerKey: "",
    midtransClientKey: "",
    midtransIsProduction: false,
    fonnteToken: "",
    googleClientId: "",
    googleClientSecret: "",
  });

  const [midtransFeesForm, setMidtransFeesForm] = useState<Array<{
    method: string;
    type: "percentage" | "static";
    value: number;
    enabled: boolean;
  }>>([]);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Sync state when settings loaded
  React.useEffect(() => {
    if (settings) {
      setPricingForm({
        pro: settings.pricing.pro,
        enterprise: settings.pricing.enterprise,
      });
      setApiKeysForm(prev => ({
        ...prev,
        midtransIsProduction: settings.midtrans.isProduction,
      }));
      // Sync midtrans fees: ambil dari platformFees + enabledMethods
      if (settings.midtrans.platformFees) {
        setMidtransFeesForm(
          settings.midtrans.platformFees.map((fee) => ({
            method: fee.method,
            type: fee.type as "percentage" | "static",
            value: fee.value,
            enabled: settings.midtrans.enabledMethods?.includes(fee.method as any) ?? true,
          }))
        );
      }
    }
  }, [settings]);

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);
    try {
      const [ip, ua] = await Promise.all([getClientIp(), getClientBrowserInfo()]);
      await updateSettings({
        adminEmail: isAuthenticated ? undefined : (adminEmail ?? undefined),
        ipAddress: ip,
        userAgent: ua,
        pricing: {
          pro: Number(pricingForm.pro),
          enterprise: Number(pricingForm.enterprise),
        },
      });
      setSuccessMsg("Harga berhasil diperbarui!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error("Update pricing error:", err);
      setErrorMsg(parseFriendlyError(err, "Gagal memperbarui harga."));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSaveMidtransFees = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);
    try {
      const [ip, ua] = await Promise.all([getClientIp(), getClientBrowserInfo()]);
      const enabledMethods = midtransFeesForm.filter(f => f.enabled).map(f => f.method as any);
      const platformFees = midtransFeesForm.map(f => ({
        method: f.method,
        type: f.type,
        value: Number(f.value),
      }));

      await updateSettings({
        adminEmail: isAuthenticated ? undefined : (adminEmail ?? undefined),
        ipAddress: ip,
        userAgent: ua,
        midtrans: {
          isProduction: apiKeysForm.midtransIsProduction,
          enabledMethods,
          platformFees,
        },
      });
      setSuccessMsg("Pengaturan metode pembayaran & biaya Midtrans berhasil disimpan!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error("Update midtrans error:", err);
      setErrorMsg(parseFriendlyError(err, "Gagal menyimpan pengaturan Midtrans."));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleResetMidtransStandard = () => {
    // Reset ke standar resmi Midtrans BI Snap
    setMidtransFeesForm([
      { method: "credit_card", type: "percentage", value: 2.9, enabled: true },
      { method: "bca_va", type: "static", value: 4000, enabled: true },
      { method: "bni_va", type: "static", value: 4000, enabled: true },
      { method: "bri_va", type: "static", value: 4000, enabled: true },
      { method: "mandiri_va", type: "static", value: 4000, enabled: true },
      { method: "permata_va", type: "static", value: 5000, enabled: true },
      { method: "qris", type: "percentage", value: 0.7, enabled: true },
      { method: "gopay", type: "percentage", value: 2.0, enabled: true },
      { method: "shopeepay", type: "percentage", value: 2.0, enabled: true },
      { method: "dana", type: "percentage", value: 1.5, enabled: true },
      { method: "ovo", type: "percentage", value: 1.5, enabled: true },
      { method: "linkaja", type: "percentage", value: 2.0, enabled: true },
      { method: "indomaret", type: "static", value: 8500, enabled: true },
      { method: "alfamart", type: "static", value: 8500, enabled: true },
    ]);
    setSuccessMsg("Berhasil dimuat ke tarif standar resmi Midtrans. Klik Simpan untuk menerapkan.");
  };

  const handleSaveApiKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);
    try {
      await updateSettings({
        adminEmail: isAuthenticated ? undefined : (adminEmail ?? undefined),
        midtrans: {
          isProduction: apiKeysForm.midtransIsProduction,
          serverKey: apiKeysForm.midtransServerKey || undefined,
          clientKey: apiKeysForm.midtransClientKey || undefined,
          enabledMethods: (settings?.midtrans.enabledMethods || []) as any,
          platformFees: settings?.midtrans.platformFees || [],
        },
        apiKeys: {
          fonnteToken: apiKeysForm.fonnteToken || undefined,
          googleClientId: apiKeysForm.googleClientId || undefined,
          googleClientSecret: apiKeysForm.googleClientSecret || undefined,
        },
      });
      setSuccessMsg("API Keys & Environment berhasil disimpan!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error("Update API keys error:", err);
      setErrorMsg(parseFriendlyError(err, "Gagal menyimpan API Keys."));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - Desktop: w-64 always visible, Mobile: Drawer */}
      <aside
        className={`fixed lg:relative top-0 left-0 z-50 h-screen w-64 bg-slate-900 text-white flex flex-col justify-between p-4 shadow-lg transition-transform duration-300 ease-in-out lg:shadow-lg ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div>
          <div className="flex items-center justify-between px-3 py-4 border-b border-slate-800 lg:justify-center">
            <div className="flex items-center space-x-2">
              <ShieldAlert className="w-6 h-6 text-indigo-400" />
              <span className="font-bold text-lg tracking-wide text-indigo-50">Superadmin</span>
            </div>
            {/* Close button mobile */}
            <button
              className="lg:hidden p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Tutup menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="mt-6 space-y-1">
            <button
              onClick={() => { setActiveTab("overview"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "overview"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>Overview</span>
            </button>

            <button
              onClick={() => { setActiveTab("apikeys"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "apikeys"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <Key className="w-4 h-4" />
              <span>API & Env Keys</span>
            </button>

            <button
              onClick={() => { setActiveTab("pricing"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "pricing"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <Tag className="w-4 h-4" />
              <span>Pengaturan Harga</span>
            </button>

            <button
              onClick={() => { setActiveTab("midtrans"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "midtrans"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <CreditCard className="w-4 h-4" />
              <span>Midtrans & BI Snap</span>
            </button>

            <button
              onClick={() => { setActiveTab("events"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "events"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>Daftar Event</span>
            </button>

            <button
              onClick={() => { setActiveTab("logs"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "logs"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <History className="w-4 h-4" />
              <span>Audit Logs</span>
            </button>

            <button
              onClick={() => { setActiveTab("password"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === "password"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <Lock className="w-4 h-4" />
              <span>Ganti Password</span>
            </button>
          </nav>
        </div>

        <div className="border-t border-slate-800 pt-4 px-2 space-y-3">
          <div>
            <div className="text-xs text-slate-400">Superadmin:</div>
            <div className="text-xs font-semibold text-slate-200 truncate">redboxart.gorontalo@gmail.com</div>
          </div>
          <button
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              try {
                await signOut();
              } catch (e) {
                console.warn("signOut error:", e);
              } finally {
                // Selalu bersihkan localStorage, walau signOut reject
                localStorage.removeItem("fotomomen_admin_email");
                setSigningOut(false);
                navigate("/admin/login", { replace: true });
              }
            }}
            className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-medium text-rose-400 hover:bg-rose-500/10 transition disabled:opacity-50"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>{signingOut ? "Memutuskan..." : "Keluar Sesi"}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto min-h-screen">
        {/* Mobile Header Bar */}
        <div className="flex items-center space-x-3 mb-6 lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition"
            aria-label="Buka menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-lg font-bold text-slate-900">Superadmin</span>
        </div>

        {/* Alerts */}
        {successMsg && (
          <div className="mb-6 flex items-center space-x-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4 shrink-0 text-emerald-600" />
            <span>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div className="mb-6 flex items-center space-x-2 bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Tab 1: Overview */}
        {activeTab === "overview" && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Overview Platform</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase">Total Event</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{stats?.totalEvents ?? 0}</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase">Total Pengguna</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{stats?.totalUsers ?? 0}</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase">Pendapatan Terbayar</div>
                <div className="text-2xl font-bold text-emerald-600 mt-1">
                  Rp {(stats?.totalRevenue ?? 0).toLocaleString("id-ID")}
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase">Status Transaksi</div>
                <div className="text-sm font-semibold text-slate-700 mt-2 flex justify-between">
                  <span>Paid: <b className="text-emerald-600">{stats?.paidOrders ?? 0}</b></span>
                  <span>Pending: <b className="text-amber-600">{stats?.pendingOrders ?? 0}</b></span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase mb-2">Total Tamu Check-In</div>
                <div className="text-2xl font-bold text-slate-900">{stats?.totalGuests ?? 0} Tamu</div>
              </div>
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase mb-2">Total Foto Event</div>
                <div className="text-2xl font-bold text-slate-900">{stats?.totalPhotos ?? 0} Foto</div>
              </div>
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs font-medium text-slate-500 uppercase mb-2">Notifikasi WA (Fonnte)</div>
                <div className="text-2xl font-bold text-slate-900">
                  {stats?.sentNotifications ?? 0} / {stats?.totalNotifications ?? 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: API & Env Keys */}
        {activeTab === "apikeys" && (
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">API Keys & Kredensial</h2>
            <p className="text-sm text-slate-500 mb-6">
              Konfigurasi kredensial production untuk Midtrans, WhatsApp Fonnte, dan Google Drive OAuth.
            </p>

            <form onSubmit={handleSaveApiKeys} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-5">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="font-semibold text-slate-800 text-sm mb-3">Midtrans Payment Gateway</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Server Key</label>
                    <input
                      type="password"
                      placeholder="SB-Mid-server-xxxx / Mid-server-xxxx"
                      value={apiKeysForm.midtransServerKey}
                      onChange={(e) => setApiKeysForm({ ...apiKeysForm, midtransServerKey: e.target.value })}
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Client Key</label>
                    <input
                      type="text"
                      placeholder="SB-Mid-client-xxxx / Mid-client-xxxx"
                      value={apiKeysForm.midtransClientKey}
                      onChange={(e) => setApiKeysForm({ ...apiKeysForm, midtransClientKey: e.target.value })}
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-1">
                    <input
                      type="checkbox"
                      id="midtransProd"
                      checked={apiKeysForm.midtransIsProduction}
                      onChange={(e) => setApiKeysForm({ ...apiKeysForm, midtransIsProduction: e.target.checked })}
                      className="rounded border-slate-300 text-indigo-600"
                    />
                    <label htmlFor="midtransProd" className="text-xs font-medium text-slate-700">
                      Aktifkan Mode Production (Live Transaksi Nyata)
                    </label>
                  </div>
                </div>
              </div>

              <div className="border-b border-slate-100 pb-4">
                <h3 className="font-semibold text-slate-800 text-sm mb-3">WhatsApp Gateway (Fonnte)</h3>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">API Token Fonnte</label>
                  <input
                    type="password"
                    placeholder="Token Fonnte Device"
                    value={apiKeysForm.fonnteToken}
                    onChange={(e) => setApiKeysForm({ ...apiKeysForm, fonnteToken: e.target.value })}
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="pb-2">
                <h3 className="font-semibold text-slate-800 text-sm mb-3">Google Drive OAuth 2.0</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Client ID</label>
                    <input
                      type="text"
                      placeholder="xxxx.apps.googleusercontent.com"
                      value={apiKeysForm.googleClientId}
                      onChange={(e) => setApiKeysForm({ ...apiKeysForm, googleClientId: e.target.value })}
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Client Secret</label>
                    <input
                      type="password"
                      placeholder="GOCSPX-xxxx"
                      value={apiKeysForm.googleClientSecret}
                      onChange={(e) => setApiKeysForm({ ...apiKeysForm, googleClientSecret: e.target.value })}
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full flex justify-center items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg text-sm font-semibold transition"
              >
                <Save className="w-4 h-4" />
                <span>Simpan Seluruh API Key</span>
              </button>
            </form>
          </div>
        )}

        {/* Tab 3: Pengaturan Harga */}
        {activeTab === "pricing" && (
          <div className="max-w-xl">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Pengaturan Harga Paket</h2>
            <p className="text-sm text-slate-500 mb-6">
              Harga di bawah ini akan otomatis digunakan oleh seluruh halaman checkout frontend & backend Midtrans payment.
            </p>

            <form onSubmit={handleSavePricing} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-1">
                  Paket Pro (IDR)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-slate-400 font-medium">Rp</span>
                  <input
                    type="number"
                    value={pricingForm.pro}
                    onChange={(e) => setPricingForm({ ...pricingForm, pro: Number(e.target.value) })}
                    className="w-full text-sm border border-slate-300 rounded-lg pl-10 pr-3 py-2 font-semibold"
                  />
                </div>
                <span className="text-xs text-slate-400">Default: Rp 99.000 (Maks 300 tamu, 1 Fotografer)</span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-800 mb-1">
                  Paket Enterprise (IDR)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-slate-400 font-medium">Rp</span>
                  <input
                    type="number"
                    value={pricingForm.enterprise}
                    onChange={(e) => setPricingForm({ ...pricingForm, enterprise: Number(e.target.value) })}
                    className="w-full text-sm border border-slate-300 rounded-lg pl-10 pr-3 py-2 font-semibold"
                  />
                </div>
                <span className="text-xs text-slate-400">Default: Rp 499.000 (Unlimited tamu, Unlimited Fotografer)</span>
              </div>

              <button
                type="submit"
                className="w-full flex justify-center items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg text-sm font-semibold transition mt-4"
              >
                <Save className="w-4 h-4" />
                <span>Simpan Perubahan Harga</span>
              </button>
            </form>
          </div>
        )}

        {/* Tab 4: Metode Pembayaran & Biaya Midtrans (Editor) */}
        {activeTab === "midtrans" && (
          <div className="max-w-3xl">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-slate-900">Metode Pembayaran & Biaya Platform</h2>
              <button
                type="button"
                onClick={handleResetMidtransStandard}
                className="flex items-center space-x-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium transition"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Muat Tarif Standar Midtrans</span>
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Aktifkan/nonaktifkan metode pembayaran, atur tipe biaya (persentase atau nilai tetap), dan nilai biaya transaksi Midtrans BI Snap.
            </p>

            <form onSubmit={handleSaveMidtransFees} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3">Aktif</th>
                      <th className="py-2.5 px-3">Metode</th>
                      <th className="py-2.5 px-3">Tipe Biaya</th>
                      <th className="py-2.5 px-3 w-32">Nilai</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {midtransFeesForm.map((fee, idx) => (
                      <tr key={fee.method} className="hover:bg-slate-50">
                        <td className="py-2.5 px-3">
                          <input
                            type="checkbox"
                            checked={fee.enabled}
                            onChange={(e) => {
                              const newFees = [...midtransFeesForm];
                              newFees[idx] = { ...newFees[idx], enabled: e.target.checked };
                              setMidtransFeesForm(newFees);
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="py-2.5 px-3 font-medium text-slate-800 uppercase">{fee.method.replace("_", " ")}</td>
                        <td className="py-2.5 px-3">
                          <select
                            value={fee.type}
                            onChange={(e) => {
                              const newFees = [...midtransFeesForm];
                              newFees[idx] = { ...newFees[idx], type: e.target.value as "percentage" | "static" };
                              setMidtransFeesForm(newFees);
                            }}
                            className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white"
                          >
                            <option value="percentage">Persen (%)</option>
                            <option value="static">Tetap (Rp)</option>
                          </select>
                        </td>
                        <td className="py-2.5 px-3">
                          <input
                            type="number"
                            step={fee.type === "percentage" ? 0.1 : 100}
                            min={0}
                            value={fee.value}
                            onChange={(e) => {
                              const newFees = [...midtransFeesForm];
                              newFees[idx] = { ...newFees[idx], value: Number(e.target.value) };
                              setMidtransFeesForm(newFees);
                            }}
                            className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs font-semibold"
                            placeholder={fee.type === "percentage" ? "Contoh: 0.7" : "Contoh: 4000"}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-xs text-slate-400 italic">Source: <a href="https://midtrans.com/pricing" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-500">midtrans.com/pricing</a> — Periksa tarif terbaru secara berkala, lalu klik "Muat Tarif Standar Midtrans"</span>
                <button
                  type="submit"
                  className="flex items-center space-x-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-xs font-semibold transition shadow-md"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Simpan Pengaturan Pembayaran</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab 5: Daftar Seluruh Event */}
        {activeTab === "events" && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Seluruh Event Platform</h2>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Nama Event</th>
                    <th className="py-3 px-4">Host / Email</th>
                    <th className="py-3 px-4">Tanggal</th>
                    <th className="py-3 px-4">Paket</th>
                    <th className="py-3 px-4">Status Bayar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allEvents?.map((ev) => (
                    <tr key={ev._id} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-semibold text-slate-900">{ev.name}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-800">{ev.hostName}</div>
                        <div className="text-[11px] text-slate-400">{ev.hostEmail}</div>
                      </td>
                      <td className="py-3 px-4">{ev.date}</td>
                      <td className="py-3 px-4 uppercase font-semibold text-indigo-600">{ev.tier}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          ev.isPaid 
                            ? "bg-emerald-100 text-emerald-800" 
                            : "bg-slate-100 text-slate-700"
                        }`}>
                          {ev.isPaid ? "Terbayar" : "Free Tier"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 6: Audit Logs */}
        {activeTab === "logs" && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Audit Logs & Aktivitas</h2>
            <p className="text-sm text-slate-500 mb-6">
              Mencatat seluruh aksi kritikal, perubahan harga, token API, dan status transaksi secara realtime.
            </p>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-600 min-w-[700px]">
                <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Waktu</th>
                    <th className="py-3 px-4">Aktor / Email</th>
                    <th className="py-3 px-4">Kategori</th>
                    <th className="py-3 px-4">Aksi</th>
                    <th className="py-3 px-4">IP Address</th>
                    <th className="py-3 px-4">Browser / Device</th>
                    <th className="py-3 px-4">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditLogs?.map((log) => (
                    <tr key={log._id} className="hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString("id-ID")}
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-800 whitespace-nowrap">
                        {log.actorEmail || "Sistem"}
                      </td>
                      <td className="py-3 px-4 uppercase font-semibold text-slate-600">
                        {log.category}
                      </td>
                      <td className="py-3 px-4 font-mono text-indigo-600 font-medium">
                        {log.action}
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                        {log.ipAddress || "-"}
                      </td>
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                        {log.userAgent || "-"}
                      </td>
                      <td className="py-3 px-4 text-slate-500 truncate max-w-xs">
                        {log.details || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 7: Ganti Password */}
        {activeTab === "password" && (
          <div className="max-w-md">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Ganti Password Admin</h2>
            <p className="text-sm text-slate-500 mb-6">
              Perbarui password akun superadmin Anda untuk keamanan akses.
            </p>

            <form onSubmit={(e) => {
              e.preventDefault();
              setSuccessMsg("Password berhasil diperbarui (Simulasi)!");
            }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Password Saat Ini</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Password Baru</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Minimal 8 karakter"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Konfirmasi Password Baru</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Ketik ulang password baru"
                />
              </div>

              <button
                type="submit"
                className="w-full flex justify-center items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg text-sm font-semibold transition mt-2"
              >
                <Lock className="w-4 h-4" />
                <span>Update Password</span>
              </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
